import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, LogOut, BriefcaseBusiness, Calendar, FileDown, Users, Video, ExternalLink, Sparkles, Clock, DollarSign, MessageSquare, Lock, TrendingUp, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { portalApi } from '@/lib/portalApi';
import NotificationBell from '../../components/NotificationBell';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import TutorPayoutMethodCard from '../../components/portal/TutorPayoutMethodCard';
import TutorLearningPlansSection from '../../components/portal/TutorLearningPlansSection';
import TutorLearningResources from '../../components/portal/TutorLearningResources';
import CollaborationHub from '../../components/portal/CollaborationHub';
import UpcomingSessionsWidget from '../../components/portal/UpcomingSessionsWidget';

const _BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _ax = axios.create({ baseURL: _BACKEND_URL, withCredentials: true });

const _money = (cents, ccy = 'AUD') => `${ccy} ${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Header entry that surfaces the tutor's PTE Reviews queue.
 * Reuses the existing shared exam-review inbox endpoint and the same
 * tutor session cookie; no separate PTE auth introduced.
 */
function PTEReviewsEntry() {
  const navigate = useNavigate();
  const [count, setCount] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await _ax.get('/api/exam/v2/tutor/reviews-inbox?exam_type=pte_academic&status=new');
        if (live) setCount(r.data?.count ?? (r.data?.items?.length ?? 0));
      } catch (_) { if (live) setCount(null); }
    })();
    return () => { live = false; };
  }, []);
  return (
    <button onClick={() => navigate('/tutor/pte')}
      className="relative h-9 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
      data-testid="nav-pte-reviews">
      <ClipboardCheck className="h-3 w-3" />
      PTE Reviews
      {typeof count === 'number' && count > 0 && (
        <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-black text-white text-[10px] font-black px-1.5"
              data-testid="nav-pte-reviews-count">
          {count}
        </span>
      )}
    </button>
  );
}

const TutorEarningsSection = ({ completedClassesCount }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await _ax.get('/api/payments/my-payments');
        if (!live) return;
        setRows(r.data?.items || []);
      } catch (_e) { setRows([]); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, []);
  const totals = rows.reduce((acc, p) => {
    const c = p.tutor_share_cents || 0;
    if (p.payout_status === 'paid') acc.paid += c;
    else acc.pending += c;
    acc.total += c;
    return acc;
  }, { paid: 0, pending: 0, total: 0 });
  return (
    <section className="space-y-4" data-testid="section-earnings">
      <TutorPayoutMethodCard />
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="h-4 w-4 text-red-700" />
        <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">Earnings & payouts</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <EarningStat label="Total Earnings" value={_money(totals.total)} tone="text-emerald-700" testid="earn-total" />
        <EarningStat label="Pending Payout" value={_money(totals.pending)} tone="text-amber-700" testid="earn-pending" />
        <EarningStat label="Paid Out" value={_money(totals.paid)} tone="text-gray-700" testid="earn-paid" />
        <EarningStat label="Completed Classes" value={completedClassesCount} testid="earn-completed-count" />
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center text-xs text-gray-400 italic py-10" data-testid="earnings-empty">No paid lessons yet. Once a student pays for a confirmed booking, your share appears here.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-gray-500 border-b">
              <tr>
                <th className="text-left py-2 pr-3">Date</th>
                <th className="text-left py-2 pr-3">Student</th>
                <th className="text-left py-2 pr-3">Lesson</th>
                <th className="text-left py-2 pr-3">Your share</th>
                <th className="text-left py-2 pr-3">Payout status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b last:border-b-0" data-testid={`earning-row-${p.id}`}>
                  <td className="py-2 pr-3 whitespace-nowrap">{p.created_at?.slice(0, 10)}</td>
                  <td className="py-2 pr-3 break-all">{p.student_email}</td>
                  <td className="py-2 pr-3 break-all">{_money(p.amount_cents, p.currency)}</td>
                  <td className="py-2 pr-3 font-bold">{_money(p.tutor_share_cents, p.currency)}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${p.payout_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                      {p.payout_status || 'pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 text-[10px] text-gray-400 leading-relaxed">
        <TrendingUp className="h-3 w-3 inline mr-1" /> Payouts are processed manually by the Winhouse team — automated payouts coming soon.
      </div>
      </div>
    </section>
  );
};

const EarningStat = ({ label, value, sub, tone = 'text-gray-900', testid }) => (
  <div className="bg-gray-50 rounded-xl border border-gray-100 p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[2px] text-gray-500 font-bold">{label}</div>
    <div className={`text-base font-bold mt-1 ${tone}`}>{value}</div>
    {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const Tab = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition ${active ? 'bg-black text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'}`}>
    {children}
  </button>
);
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_e) { return iso || '—'; } };
const Empty = ({ children, testid }) => (
  <div className="text-center text-xs text-gray-400 italic py-10" data-testid={testid}>{children}</div>
);

const ClassCard = ({ c }) => (
  <div className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-red-200 transition" data-testid={`class-row-${c.booking_id}`}>
    <div className="h-10 w-10 rounded-lg bg-black text-[#D3D86F] flex items-center justify-center flex-shrink-0">
      <Video className="h-4 w-4" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-bold text-gray-900 truncate">{c.subject}</div>
      <div className="text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
        <Clock className="h-3 w-3" /> {fmt(c.start_time)}
        <span className="text-gray-300">·</span>
        <span>with {c.student_name}</span>
        {c.status && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[9px] font-bold uppercase tracking-wider ml-1">{c.status}</span>}
      </div>
    </div>
    {c.join_path && (
      <a href={c.join_path} target="_blank" rel="noopener noreferrer"
        className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
        data-testid={`class-join-${c.booking_id}`}>
        <ExternalLink className="h-3 w-3" /> Open
      </a>
    )}
  </div>
);

const RecapCard = ({ r }) => (
  <div className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl group hover:border-red-200 transition" data-testid={`recap-row-${r.recap_id}`}>
    <div className="h-10 w-10 rounded-lg bg-black text-[#D3D86F] flex items-center justify-center flex-shrink-0">
      <FileDown className="h-4 w-4" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-bold text-gray-900 truncate flex items-center gap-1.5">
        {r.filename}
        {r.sections_used?.ai && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-black text-[#D3D86F] text-[9px] font-bold uppercase tracking-wider">
            <Sparkles className="h-2.5 w-2.5" /> AI
          </span>
        )}
      </div>
      <div className="text-[11px] text-gray-500">{r.lesson_track || r.subject} · {fmt(r.generated_at)} · {r.student_name}</div>
    </div>
    {r.download_url && (
      <a href={r.download_url} target="_blank" rel="noopener noreferrer"
        className="h-9 px-4 rounded-full bg-black hover:bg-gray-800 text-white text-[11px] font-bold uppercase tracking-wider">Download</a>
    )}
  </div>
);

const TutorDashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loadingData, setLoadingData] = useState(true);
  const [pwOpen, setPwOpen] = useState(false);

  const refresh = useCallback(async () => {
    try { setData(await portalApi.myDashboard()); } catch (_e) {} finally { setLoadingData(false); }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/tutor/login', { replace: true }); return; }
    const role = (user.role || '').toLowerCase();
    if (role === 'student' || role === 'parent') { navigate('/student/dashboard', { replace: true }); return; }
    if (role === 'admin' || role === 'owner') { navigate('/admin/dashboard', { replace: true }); return; }
    if (['staff', 'support', 'finance'].includes(role)) { navigate('/staff/dashboard', { replace: true }); return; }
    // Phase 4D — tutors who logged in with a temporary admin-issued password
    // MUST change it before reaching the dashboard.
    if (user.force_password_change) {
      navigate('/tutor/force-change-password', { replace: true });
      return;
    }
    refresh();
  }, [loading, user, navigate, refresh]);

  if (loading || loadingData || !user) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>;
  }

  const totals = data?.totals || {};
  const upcoming = data?.upcoming_classes || [];
  const completed = data?.completed_classes || [];
  const students = data?.students || [];
  const recaps = data?.recent_recaps || [];

  const Stat = ({ label, value, testid }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[2px] text-gray-500 font-bold">{label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value ?? 0}</div>
    </div>
  );

  return (
    <>
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="tutor-dashboard">
      <header className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BriefcaseBusiness className="h-5 w-5 text-[#D3D86F]" />
            <div>
              <div className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] font-bold">WINHOUSE TUTOR PORTAL</div>
              <div className="text-sm font-bold">{user.name || user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell testidPrefix="tutor-" />
            <button onClick={async () => {
                try {
                  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                  const r = await fetch(`${BACKEND_URL}/api/me/tutor/timetable.pdf`, { credentials: 'include' });
                  if (!r.ok) throw new Error('Download failed');
                  const blob = await r.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = 'teaching-timetable.pdf';
                  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                } catch (_e) { /* silent */ }
              }}
              className="h-9 px-4 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="tutor-timetable-pdf">
              <FileDown className="h-3 w-3" /> Timetable PDF
            </button>
            <button onClick={() => navigate('/tutor/schedule')}
              className="h-9 px-4 rounded-full bg-[#D3D86F] hover:bg-[#c4ca54] text-black text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="nav-schedule">
              <Calendar className="h-3 w-3" /> My Schedule
            </button>
            <PTEReviewsEntry />
            <button onClick={() => navigate('/staff/dashboard')}
              className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="nav-staff-hub">
              <MessageSquare className="h-3 w-3" /> Staff Hub
            </button>
            <button onClick={() => setPwOpen(true)}
              className="h-9 px-4 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="nav-change-password">
              <Lock className="h-3 w-3" /> Change password
            </button>
            <button onClick={() => { logout(); navigate('/tutor/login'); }}
              className="h-9 px-4 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="portal-logout">
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Classes" value={totals.classes} testid="stat-classes" />
          <Stat label="Students" value={totals.students} testid="stat-students" />
          <Stat label="Recaps" value={totals.recaps} testid="stat-recaps" />
          <Stat label="Upcoming" value={upcoming.length} testid="stat-upcoming" />
        </div>

        {/* Phase 5F — Tutor Learning Plans (only own students) */}
        <TutorLearningPlansSection />
        <UpcomingSessionsWidget role="tutor" />

        <div className="flex items-center gap-2 flex-wrap" data-testid="tutor-tabs">
          <Tab active={tab === 'overview'} onClick={() => setTab('overview')} testid="tab-overview">Overview</Tab>
          <Tab active={tab === 'classes'} onClick={() => setTab('classes')} testid="tab-classes">Classes</Tab>
          <Tab active={tab === 'students'} onClick={() => setTab('students')} testid="tab-students">Students</Tab>
          <Tab active={tab === 'learning'} onClick={() => setTab('learning')} testid="tab-learning">Learning Resources</Tab>
          <Tab active={tab === 'hub'} onClick={() => setTab('hub')} testid="tab-hub">Hub</Tab>
          <Tab active={tab === 'recaps'} onClick={() => setTab('recaps')} testid="tab-recaps">Recaps</Tab>
          <Tab active={tab === 'earnings'} onClick={() => setTab('earnings')} testid="tab-earnings">Earnings</Tab>
        </div>

        {tab === 'overview' && (
          <div className="grid lg:grid-cols-2 gap-6">
            <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="section-upcoming">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4 text-red-700" />
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">Next up</div>
              </div>
              {upcoming.length === 0 && <Empty testid="empty-upcoming">No upcoming classes yet.</Empty>}
              <div className="space-y-2">{upcoming.slice(0, 3).map((c) => <ClassCard key={c.booking_id} c={c} />)}</div>
            </section>
            <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="section-students">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-4 w-4 text-red-700" />
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">Your students</div>
              </div>
              {students.length === 0 && <Empty>No students assigned yet.</Empty>}
              <div className="space-y-2">{students.slice(0, 5).map((s) => (
                <div key={s.student_email} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-lg" data-testid={`student-row-${s.student_email}`}>
                  <div className="h-9 w-9 rounded-full bg-teal-700 text-white flex items-center justify-center text-xs font-bold">{(s.student_name || s.student_email || '?').slice(0, 2).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-gray-900 truncate">{s.student_name || s.student_email}</div>
                    <div className="text-[10px] text-gray-500">{s.total_classes} class{s.total_classes === 1 ? '' : 'es'} · last {fmt(s.last_class_at)}</div>
                  </div>
                </div>
              ))}</div>
            </section>
          </div>
        )}

        {tab === 'classes' && (
          <div className="space-y-6">
            <section className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Upcoming · {upcoming.length}</div>
              {upcoming.length === 0 && <Empty>No upcoming classes.</Empty>}
              <div className="space-y-2">{upcoming.map((c) => <ClassCard key={c.booking_id} c={c} />)}</div>
            </section>
            <section className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Completed · {completed.length}</div>
              {completed.length === 0 && <Empty>No completed classes yet.</Empty>}
              <div className="space-y-2">{completed.map((c) => <ClassCard key={c.booking_id} c={c} />)}</div>
            </section>
          </div>
        )}

        {tab === 'students' && (
          <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="section-students-full">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">All students · {students.length}</div>
            {students.length === 0 && <Empty>No students assigned yet.</Empty>}
            <div className="grid sm:grid-cols-2 gap-3">{students.map((s) => (
              <div key={s.student_email} className="p-4 bg-white border border-gray-200 rounded-xl" data-testid={`student-card-${s.student_email}`}>
                <div className="text-sm font-bold text-gray-900">{s.student_name || s.student_email}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{s.student_email}</div>
                <div className="text-[11px] text-gray-700 mt-2">{s.total_classes} class{s.total_classes === 1 ? '' : 'es'} · {s.completed_classes} completed</div>
                <div className="text-[10px] text-gray-500 mt-0.5">Subjects: {(s.subjects || []).join(', ') || '—'}</div>
              </div>
            ))}</div>
          </section>
        )}

        {tab === 'recaps' && (
          <section className="bg-white border border-gray-200 rounded-2xl p-5">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">All recaps · {recaps.length}</div>
            {recaps.length === 0 && <Empty>No recaps yet.</Empty>}
            <div className="space-y-2">{recaps.map((r) => <RecapCard key={r.recap_id} r={r} />)}</div>
          </section>
        )}

        {tab === 'learning' && (
          <TutorLearningResources />
        )}

        {tab === 'hub' && (
          <div data-testid="tab-content-hub"><CollaborationHub /></div>
        )}

        {tab === 'earnings' && (
          <TutorEarningsSection completedClassesCount={completed.length} />
        )}
      </main>
    </div>
    <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
};

export default TutorDashboardPage;
